import { JSX, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  ImageField,
  Image,
  RichText,
  RichTextField,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { classNames } from 'assets/utils/helper';
import { getMarginSize } from 'assets/utils/getMarginSize';

type Quote = {
  fields: {
    MarginSize: {
      fields: {
        heading: {
          value: string;
        };
      };
    };
    image: ImageField;
    person: {
      value: string;
    };
    quotetext: RichTextField;
  };
};

type QuoteChildProps = {
  current: boolean;
  backgroundcolor: string;
  handleTouchStart: any;
  handleTouchEnd: any;
  index: number;
  next: boolean;
  prev: boolean;
  quote: Quote;
  swipeLeft: boolean;
  swipeRight: boolean;
  quoteNum: number;
  firstIndex: number;
  lastIndex: number;
  uid: string;
  numQuotes: number;
  looping: boolean;
};

const QuoteChild = (props: QuoteChildProps): JSX.Element => {
  const marginSize = getMarginSize(
    props?.quote?.fields?.MarginSize?.fields?.heading?.value
  );
  const {
    current,
    firstIndex,
    backgroundcolor,
    next,
    prev,
    quote,
    index,
    quoteNum,
    uid,
    swipeLeft,
    swipeRight,
    handleTouchEnd,
    handleTouchStart,
    numQuotes,
    looping,
  } = props;
  const elementRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  const [leftOffset, setLeftOffset] = useState(0);
  const firstIndexRef = useRef(firstIndex);

  const handleResize = () => {
    if (elementRef.current) {
      const rect = elementRef.current?.getBoundingClientRect();
      setWidth(rect.width);
      setLeftOffset(firstIndexRef.current * rect.width);
    }
  };

  useEffect(() => {
    firstIndexRef.current = firstIndex;
  }, [firstIndex]);

  useLayoutEffect(() => {
    if (elementRef.current) {
      const rect = elementRef.current?.getBoundingClientRect();
      setWidth(rect.width);
    }
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
    }
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (numQuotes > 1 && index == -(numQuotes - 1)) {
      setLeftOffset(width);
    } else {
      setLeftOffset(firstIndex * width);
    }
  }, [swipeLeft, swipeRight, index]);

  return (
    <div
      ref={elementRef}
      style={{
        left: leftOffset,
        display: looping && index != 0 ? 'none' : 'flex',
      }}
      className={classNames(
        'quote-child',
        backgroundcolor.toLowerCase(),
        current ? 'current' : prev ? 'prev' : next ? 'next' : '',
        marginSize
      )}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      key={`${uid}-${quoteNum}`}
    >
      {quote.fields.image && <Image field={quote.fields.image.value} />}
      <div
        className={`quote-container responsive-inner-side-spacing ${backgroundcolor === 'Dark' ? 'bg-primary-dark' : backgroundcolor === 'Gradient' ? 'gradient-bg-dark-responsive' : 'bg-secondary'}`}
      >
        <RichText
          field={quote.fields.quotetext}
          className="osf-body-copy quote"
        />
        <p className="medium-bold">{quote.fields.person.value}</p>
      </div>
    </div>
  );
};

export default QuoteChild;
